import { DEFAULT_RENTAL_ORIGINATION_POINTS } from './financingDefaults';

/**
 * LIVE RENTAL MATH — the instant, on-screen arithmetic.
 * ====================================================
 *
 * Every function here MIRRORS backend/rental_engine.py. The backend stays authoritative:
 * pressing "Apply Changes & Recalculate" replaces these numbers with its results. What
 * this module buys is that sums and derived figures move AS YOU TYPE, instead of the
 * page sitting frozen until a server round-trip.
 *
 * The split is deliberate:
 *   - Pure arithmetic over values already on screen (opex totals, closing costs, payment,
 *     NOI, cash flow, DSCR, cap rate, cash-on-cash) -> here, instant, no network.
 *   - Anything needing the comp set (ARV / market value) -> backend only. It cannot be
 *     derived locally, so it never moves until Apply.
 *
 * IF YOU CHANGE A FORMULA HERE, CHANGE IT IN rental_engine.py TOO — a divergence shows up
 * as numbers that shift the moment the user presses Apply, which reads as a bug in both.
 *
 * `??` not `||` throughout: 0 is a legitimate value for every percentage below
 * (self-managing, no vacancy allowance, 0 points) and must never fall through to a default.
 */

const n = (v, fallback = 0) => {
  if (v === null || v === undefined || v === '') return fallback;
  const parsed = typeof v === 'string' ? parseFloat(String(v).replace(/[$,\s]/g, '')) : Number(v);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Standard amortising P&I. Mirrors rental_engine.calculate_monthly_payment. */
export function monthlyPayment(loanAmount, annualRatePct, termYears) {
  const P = n(loanAmount);
  const rate = n(annualRatePct);
  const years = n(termYears, 30);
  if (P <= 0 || rate <= 0) return 0;
  const r = rate / 100 / 12;
  const nPayments = years * 12;
  if (r <= 0) return P / nPayments;
  const pow = Math.pow(1 + r, nPayments);
  return (P * (r * pow)) / (pow - 1);
}

/**
 * CASH-OUT REFINANCE PROCEEDS — one formula, used by every screen that shows the figure.
 *
 * Mirrors backend/server.py compute_refinance_terms(). Three screens were each doing their
 * own arithmetic and disagreeing by tens of thousands of dollars on the same deal:
 *   input page      loan - payoff                                   -> $97,902
 *   backend         loan - payoff - closing - origination           -> $91,025
 *   analysis page   loan - 0      - closing - origination           -> $165,025
 * The middle one is right: refinancing costs money, and the payoff is not optional.
 *
 * Closing and origination are charged against the LOAN on a refinance — there is no
 * purchase price to charge them against.
 */
export function refinanceCashOut({ appraisedValue, ltvPct, existingPayoff,
                                   closingCostsPct = 3, originationPoints = 1 }) {
  const loan = n(appraisedValue) * (n(ltvPct, 75) / 100);
  const closing = loan * (n(closingCostsPct, 3) / 100);
  const origination = loan * (n(originationPoints, 1) / 100);
  const cashOut = loan - n(existingPayoff) - closing - origination;
  return { loan, closing, origination, cashOut };
}

/**
 * Compute every rental figure that can be known without the server.
 * `a` is the assumptions bundle (camelCase, as held in Results.js state).
 */
/**
 * DSCR ELIGIBILITY — the ONE definition on the frontend.
 *
 * Mirrors DSCR_INELIGIBLE_CONDITIONS in rental_engine.py. These are the ENUM VALUES, not
 * the member names: RentalPropertyCondition.HEAVY_REHAB = "heavy". A heavy-rehab or
 * distressed property cannot carry a DSCR loan, so the backend rewrites financing_type to
 * "cash"; anything on this side that disagrees produces a screen full of financed numbers
 * under a "Cash" label.
 *
 * Exported so the input page tests the SAME rule rather than its own. Analyzer.js used to
 * use `condition === 'turnkey'`, which is stricter than the engine and would have denied
 * DSCR on light and moderate properties that real lenders finance.
 */
export const DSCR_INELIGIBLE_CONDITIONS = ['heavy', 'distressed'];

/** The five conditions the engine understands, with their display labels. */
export const RENTAL_CONDITIONS = [
  { value: 'turnkey',    en: 'Turnkey',    es: 'Listo' },
  { value: 'light',      en: 'Light',      es: 'Leve' },
  { value: 'moderate',   en: 'Moderate',   es: 'Moderado' },
  { value: 'heavy',      en: 'Heavy',      es: 'Pesado' },
  { value: 'distressed', en: 'Distressed', es: 'En mal estado' },
];

export function isDscrEligible(condition) {
  return !DSCR_INELIGIBLE_CONDITIONS.includes(
    String(condition || '').toLowerCase()
  );
}

export function computeLiveRental(a = {}, ctx = {}) {
  const purchasePrice = n(ctx.purchasePrice);
  const monthlyRent = n(a.rentalMonthlyRent);
  const annualRent = monthlyRent * 12;

  // ---- Operating expenses (annual). Mirrors RentalCosts.total_annual.
  const annualTaxes = n(a.rentalAnnualTaxes);
  const annualInsurance = n(a.rentalAnnualInsurance);
  const annualUtilities = n(a.rentalUtilities);
  const vacancy = annualRent * (n(a.rentalVacancyPct) / 100);
  const maintenance = annualRent * (n(a.rentalMaintenancePct) / 100);
  const management = annualRent * (n(a.rentalManagementPct) / 100);
  const annualHoa = n(a.rentalHoa) * 12;          // HOA is entered MONTHLY
  const annualOtherOpex = n(a.rentalOtherOpex) * 12; // other opex is entered MONTHLY
  const annualOpex = annualTaxes + annualInsurance + annualUtilities
    + vacancy + maintenance + management + annualHoa + annualOtherOpex;

  // ---- NOI
  const noi = annualRent - annualOpex;

  // ---- Financing
  const isRefinance = (a.rentalPurchaseType === 'refinancing');

  const dscrEligible = isDscrEligible(a.rentalPropertyCondition);
  const requestedCash = (a.rentalFinancingType === 'cash');
  const isCash = requestedCash || !dscrEligible;
  // Financing was decided by the CONDITION, not by the user — say so.
  //
  // This was `!requestedCash && !dscrEligible`, which never fired: the input page
  // switches the selection to cash as soon as the condition rules DSCR out, so by the
  // time the analysis runs the request always says "cash" and the reason was lost. Key
  // it off eligibility alone. On an ineligible property the cash treatment is imposed
  // whatever the user picked, and that is the thing worth stating.
  const financingForcedToCash = !dscrEligible;
  const financingType = isCash ? 'cash' : 'dscr';
  const downPaymentPct = isRefinance ? 0 : n(a.rentalDownPaymentPct, 25);
  const appraised = n(a.rentalAppraisedValue, purchasePrice);
  const basis = isRefinance ? appraised : purchasePrice;

  let loanAmount = 0;
  let downPaymentAmount = 0;
  if (isCash) {
    downPaymentAmount = basis;           // all cash: the whole price is "down"
    loanAmount = 0;
  } else if (isRefinance) {
    loanAmount = appraised * (n(a.rentalRefiLtv, 75) / 100);
    downPaymentAmount = 0;               // you already own it
  } else {
    downPaymentAmount = basis * (downPaymentPct / 100);
    loanAmount = basis - downPaymentAmount;
  }

  const payment = isCash ? 0 : monthlyPayment(loanAmount, n(a.rentalInterestRate, 7.5), n(a.rentalLoanTermYears, 30));
  const annualDebtService = payment * 12;

  // ---- Closing costs. Mirrors the closing_costs_breakdown branch of rental_engine.
  // On a REFINANCE the base closing cost is a percentage of the LOAN, not of the property
  // value — there is no purchase price to charge against. (compute_refinance_terms does
  // the same; charging it against the value understated cash-out by the difference.)
  const baseClosing = (isRefinance ? loanAmount : basis) * (n(a.rentalClosingCostsPct, 3) / 100);
  const originationCost = loanAmount * (n(a.rentalOriginationPoints, isCash ? 0 : DEFAULT_RENTAL_ORIGINATION_POINTS) / 100);
  const lenderFees = isCash ? 0 : n(a.rentalLenderFees);
  const titleAttorney = n(a.rentalTitleAttorneyFees);
  const escrowReserves = n(a.rentalEscrowReserves);
  const otherClosing = n(a.rentalOtherClosingCosts);
  const sellerCommission = basis * (n(a.rentalSellerAgentCommission) / 100);
  const closingCosts = baseClosing + originationCost + lenderFees
    + titleAttorney + escrowReserves + otherClosing + sellerCommission;

  // ---- Cash position
  // A refinance invests nothing: cash comes OUT (loan, less old payoff and refi costs).
  // Use the shared helper so this screen cannot drift from the input page or the backend.
  const existingPayoff = n(a.rentalExistingPayoff);
  const refiCashOut = isRefinance
    ? refinanceCashOut({
        appraisedValue: appraised, ltvPct: n(a.rentalRefiLtv, 75), existingPayoff,
        closingCostsPct: n(a.rentalClosingCostsPct, 3),
        originationPoints: n(a.rentalOriginationPoints, DEFAULT_RENTAL_ORIGINATION_POINTS),
      }).cashOut
    : 0;
  const cashInvested = isRefinance ? 0 : downPaymentAmount + closingCosts;

  // ---- VALUE-DEPENDENT figures.
  // `marketValue` is the ARV — our comp-derived estimate, or the user's override. What it
  // drives differs sharply by mode, and conflating the two would put ARV in charge of a
  // payment it has nothing to do with:
  //   REFINANCE — the loan is written against the appraised value, so ARV already drives
  //               loan -> P&I -> PITIA -> DSCR -> cash flow -> cash-out (above), and
  //               equity is the value less what is still owed.
  //   PURCHASE  — the loan comes off the PURCHASE PRICE. ARV must NOT touch the payment;
  //               it drives only equity and how the price compares to the market.
  const marketValue = n(ctx.marketValue, isRefinance ? appraised : purchasePrice);
  const equity = isRefinance
    ? marketValue - existingPayoff
    : marketValue - purchasePrice;
  const priceVsMarketPct = (!isRefinance && marketValue > 0)
    ? (purchasePrice / marketValue) * 100
    : 0;

  // ---- Returns
  const annualCashFlow = noi - annualDebtService;
  const monthlyCashFlow = annualCashFlow / 12;
  const capRate = basis > 0 ? (noi / basis) * 100 : 0;
  const dscr = annualDebtService > 0 ? noi / annualDebtService : 0;
  const cashOnCashApplicable = !isRefinance && cashInvested > 0;
  const cashOnCash = cashOnCashApplicable ? (annualCashFlow / cashInvested) * 100 : 0;

  // ---- PITIA: THE PAYMENT. P&I + Taxes + Insurance + Association (HOA).
  // What the owner actually pays each month. Taxes, insurance and HOA live HERE and
  // nowhere else; `monthlyOtherOpex` below deliberately excludes them.
  const mTaxes = annualTaxes / 12;
  const mIns = annualInsurance / 12;
  const mHoa = annualHoa / 12;
  const monthlyEscrow = mTaxes + mIns;            // taxes + insurance alone (cash-mode carry)
  const monthlyPiti = payment + monthlyEscrow;    // legacy, omits HOA
  const monthlyPitia = payment + mTaxes + mIns + mHoa;
  const monthlyOtherOpex = (annualOpex / 12) - mTaxes - mIns - mHoa;

  // DSCR = RENT / PITIA. 1.00 = the rent exactly covers the FULL payment. A cash purchase
  // has no loan, so no debt to service and no ratio.
  const dscrPitia = (payment > 0 && monthlyPitia > 0) ? monthlyRent / monthlyPitia : 0;

  // THE INVARIANT, mirrored from the backend: rent - PITIA - otherOpex must equal
  // NOI/12 - P&I. They diverge only if a cost is counted twice.
  if (process.env.NODE_ENV !== 'production') {
    const twoWays = monthlyRent - monthlyPitia - monthlyOtherOpex;
    if (Math.abs(twoWays - (noi / 12 - payment)) > 0.02) {
      // eslint-disable-next-line no-console
      console.error('[rentalMath] DOUBLE COUNT: rent-PITIA-other=%s but NOI/12-P&I=%s',
        twoWays.toFixed(2), (noi / 12 - payment).toFixed(2));
    }
  }

  // Mirror the backend's ROUNDING as well as its formulas. rental_engine rounds dscr,
  // cap_rate and cash_on_cash to 2dp and monetary values to 2dp; leaving these unrounded
  // put the live figure a hair off the backend's (14.048 vs 14.05), which is invisible
  // until it lands on a display boundary and the number appears to change on Apply.
  // `+ 0` collapses negative zero: float subtraction can leave -0, which renders as
  // "-$0" and reads like a bug to anyone looking at their own numbers.
  const r2 = (v) => (Math.round((Number(v) + Number.EPSILON) * 100) / 100) + 0;

  return {
    annualRent, monthlyRent,
    annualOpex: r2(annualOpex), monthlyOpex: r2(annualOpex / 12),
    opexBreakdown: {
      property_taxes: annualTaxes, insurance: annualInsurance, utilities: annualUtilities,
      vacancy_reserve: vacancy, maintenance_reserve: maintenance,
      property_management: management, hoa: annualHoa, other: annualOtherOpex,
    },
    noi: r2(noi),
    loanAmount: r2(loanAmount), downPaymentAmount: r2(downPaymentAmount), downPaymentPct,
    monthlyDebtService: r2(payment), annualDebtService: r2(annualDebtService),
    monthlyEscrow: r2(monthlyEscrow), monthlyPiti: r2(monthlyPiti),
    monthlyPitia: r2(monthlyPitia), monthlyOtherOpex: r2(monthlyOtherOpex),
    closingCosts: r2(closingCosts),
    closingBreakdown: {
      base_closing_costs: baseClosing, origination_points: originationCost,
      lender_fees: lenderFees, title_attorney_fees: titleAttorney,
      escrow_reserves: escrowReserves, other_closing_costs: otherClosing,
      seller_agent_commission: sellerCommission,
      total: r2(closingCosts),
    },
    cashInvested: r2(cashInvested), refiCashOut: r2(refiCashOut), isRefinance, isCash,
    financingType, dscrEligible, financingForcedToCash,
    marketValue: r2(marketValue), equity: r2(equity), priceVsMarketPct: r2(priceVsMarketPct),
    annualCashFlow: r2(annualCashFlow), monthlyCashFlow: r2(monthlyCashFlow),
    capRate: r2(capRate), dscr: r2(dscrPitia), cashOnCash: r2(cashOnCash), cashOnCashApplicable,
  };
}

export default { computeLiveRental, monthlyPayment };
